import React, { useEffect, useRef } from "react";

// ─── Leaflet loader ───────────────────────────────────────────────────────────
const loadLeaflet = () =>
    new Promise((resolve) => {
      if (window.L) return resolve(window.L);
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      const s = document.createElement("script");
      s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      s.onload = () => resolve(window.L);
      document.head.appendChild(s);
    });

// ─── Leaflet Route Map ────────────────────────────────────────────────────────
// routePoints: { lat, lng, datetime }[] — datetime is only shown on the first/last point's tooltip.
const RouteMap = ({ routePoints }) => {
  const divRef    = useRef(null);
  const mapRef    = useRef(null);
  const layersRef = useRef([]);

  useEffect(() => {
    if (!routePoints?.length || !divRef.current) return;
      loadLeaflet().then((L) => {
          if (!mapRef.current) {
              mapRef.current = L.map(divRef.current, { zoomControl: true });
              L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                  attribution: "© OpenStreetMap", maxZoom: 19,
              }).addTo(mapRef.current);
          }

          layersRef.current.forEach((l) => mapRef.current.removeLayer(l));
          layersRef.current = [];

          const latlngs = routePoints
              .filter((p) => p?.lat != null && p?.lng != null)
              .map((p) => [parseFloat(p.lat), parseFloat(p.lng)]);

          if (!latlngs.length) return;

          const poly = L.polyline(latlngs, { color: "#3b82f6", weight: 4, opacity: 0.95 });
          poly.addTo(mapRef.current);

          const mkIcon = (bg) => L.divIcon({
              html: `<div style="width:13px;height:13px;background:${bg};border:2.5px solid #fff;border-radius:50%;box-shadow:0 1px 5px rgba(0,0,0,0.6)"></div>`,
              iconSize: [13, 13], iconAnchor: [6, 6], className: "",
          });

          const startLabel = routePoints[0]?.datetime || "Start";
          const endLabel   = routePoints[routePoints.length - 1]?.datetime || "End";

          const sm = L.marker(latlngs[0], { icon: mkIcon("#22c55e") })
              .bindTooltip(startLabel, { permanent: true, direction: "top", offset: [0, -8] });

          const em = L.marker(latlngs[latlngs.length - 1], { icon: mkIcon("#ef4444") })
              .bindTooltip(endLabel, { permanent: true, direction: "top", offset: [0, -8] });

          sm.addTo(mapRef.current);
          em.addTo(mapRef.current);

          layersRef.current = [poly, sm, em];

          setTimeout(() => {
              mapRef.current.invalidateSize();
              mapRef.current.fitBounds(poly.getBounds(), { padding: [28, 28] });
          }, 200);
      });
  }, [routePoints]);

  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);

  if (!routePoints?.length) {
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", height: "100%", color: "#555", gap: 8 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
               stroke="#555" strokeWidth="1.5" strokeLinecap="round">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
          <span style={{ fontSize: 13 }}>No route coordinates available</span>
        </div>
    );
  }

  return <div ref={divRef} style={{ width: "100%", height: "100%", minHeight: "400px" }} />;
};

export default RouteMap;
